import { Link } from "react-router-dom";
import { ArrowUpRight, Star, Award } from "lucide-react";

const KIND_LABELS = {
  ota: "OTA",
  meta: "Meta-search",
  chain: "Chain loyalty",
};

// Score → 0-100 stroke-dasharray
function scoreToOffset(score) {
  const pct = Math.max(0, Math.min(10, score)) / 10;
  const circumference = 2 * Math.PI * 20; // r=20
  return circumference - pct * circumference;
}

export default function OperatorCard({ operator, compact = false, variant = "default", featured = false }) {
  const color = operator.color || "#C86B53";

  if (variant === "hero") {
    return (
      <Link
        to={`/operators/${operator.key}`}
        data-testid={`operator-card-${operator.key}`}
        className={`group relative block overflow-hidden text-sand ${featured ? "ring-2 ring-offset-2 ring-offset-sand ring-terracotta" : ""}`}
      >
        {featured && (
          <div className="absolute top-4 left-4 z-10 bg-terracotta text-sand text-[10px] uppercase tracking-[0.2em] px-2.5 py-1.5 flex items-center gap-1.5" data-testid="featured-badge">
            <Award size={12} /> Editor's pick
          </div>
        )}
        <div className="relative aspect-[4/5] md:aspect-[5/6] overflow-hidden">
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${color} 0%, ${color}cc 40%, #1a1a1a 100%)`,
            }}
          />
          <img
            src={operator.image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-60 transition-transform duration-[1200ms] group-hover:scale-[1.05]"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent" />
          <div className="relative h-full flex flex-col justify-between p-7 md:p-10">
            <div>
              <div className="overline text-sand/70">{KIND_LABELS[operator.kind]}</div>
              <div className="font-serif text-4xl md:text-5xl leading-[0.95] tracking-tight mt-3">
                {operator.name}
              </div>
            </div>
            <div className="flex items-end justify-between gap-4">
              <div>
                <div className="overline text-sand/60 mb-1">Editor</div>
                <div className="font-serif text-5xl font-medium leading-none">
                  {operator.editor_score.toFixed(1)}
                  <span className="text-base text-sand/50 font-sans ml-1">/10</span>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] bg-sand text-charcoal px-4 py-2.5 group-hover:bg-terracotta group-hover:text-sand transition-colors">
                Deep dive <ArrowUpRight size={13} />
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/operators/${operator.key}`}
      data-testid={`operator-card-${operator.key}`}
      className={`group relative block overflow-hidden bg-white border ${featured ? "border-terracotta shadow-[0_0_0_1px_#C86B53]" : "border-charcoal/10"} hover:-translate-y-1 transition-all duration-500 ease-out`}
    >
      {featured && (
        <div className="absolute top-3 right-3 z-10 bg-terracotta text-sand text-[9px] uppercase tracking-[0.18em] px-2 py-1 flex items-center gap-1" data-testid="featured-badge-compact">
          <Award size={10} /> Editor's pick
        </div>
      )}
      {/* Top brand-color wash */}
      <div
        className="relative h-20 overflow-hidden"
        style={{ background: `linear-gradient(120deg, ${color} 0%, ${color}dd 100%)` }}
      >
        <div className="absolute inset-0 noise-overlay opacity-30" />
        <div className="relative h-full px-5 md:px-6 flex items-center justify-between text-sand">
          <div className="text-[10px] uppercase tracking-[0.22em] opacity-85">
            {KIND_LABELS[operator.kind]}
          </div>
          {/* Score ring */}
          <svg width="48" height="48" viewBox="0 0 48 48" className="shrink-0">
            <circle cx="24" cy="24" r="20" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="3" />
            <circle
              cx="24" cy="24" r="20" fill="none"
              stroke="#ffffff" strokeWidth="3" strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 20}
              strokeDashoffset={scoreToOffset(operator.editor_score)}
              transform="rotate(-90 24 24)"
              className="transition-[stroke-dashoffset] duration-700"
            />
            <text x="24" y="28" textAnchor="middle" className="font-mono" fill="#ffffff" fontSize="13" fontWeight="600">
              {operator.editor_score.toFixed(1)}
            </text>
          </svg>
        </div>
      </div>

      <div className="p-5 md:p-6">
        <div className="font-serif text-2xl md:text-3xl leading-tight text-charcoal group-hover:text-terracotta transition-colors">
          {operator.name}
        </div>
        {!compact && (
          <p className="mt-2 text-sm text-charcoal/65 line-clamp-2">{operator.tagline}</p>
        )}

        <div className="mt-5 space-y-2 text-xs">
          <div className="flex items-center gap-2">
            <Award size={12} className="text-terracotta shrink-0" />
            <span className="text-charcoal/80 truncate">{operator.loyalty?.name || "No loyalty program"}</span>
          </div>
          <div className="flex items-center gap-2">
            <Star size={12} className="text-terracotta shrink-0 fill-terracotta/30" />
            <span className="text-charcoal/80 truncate">{operator.inventory}</span>
          </div>
        </div>

        {/* Best-for pills */}
        {!compact && operator.best_for?.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-1.5">
            {operator.best_for.slice(0, 2).map((b) => (
              <span
                key={b}
                className="text-[10px] uppercase tracking-[0.14em] px-2 py-1 border border-charcoal/15 text-charcoal/75"
              >
                {b}
              </span>
            ))}
          </div>
        )}

        <div className="mt-6 flex items-center justify-between text-xs uppercase tracking-[0.18em] text-charcoal/60 group-hover:text-terracotta">
          <span>Read review</span>
          <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
        </div>
      </div>
    </Link>
  );
}
